import React from 'react';
import { motion } from 'framer-motion';
import { SparkleEffect } from '../components/SparkleEffect';
import { CheckIcon, SparklesIcon, LockIcon } from 'lucide-react';
import { GlitterHeart } from '../components/GlitterHeart';
export function CoursesPage() {
  return <div className="min-h-screen bg-white pt-24 pb-24" data-id="element-378">
      {/* Hero */}
      <section className="relative py-20 text-center overflow-hidden" data-id="element-379">
        <SparkleEffect count={90} data-id="element-380" />
        <div className="max-w-3xl mx-auto px-4 relative z-10" data-id="element-381">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-5xl md:text-6xl font-bold text-plum-700 mb-6" data-id="element-382">
            The Broken Beginning
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-plum-500 font-medium" data-id="element-383">
            Real tools for real transformation. No fluff, no gatekeeping.
          </motion.p>
        </div>
      </section>

      {/* Featured Course */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-384">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative bg-gradient-to-br from-blush-100 to-white border border-blush-200 rounded-[3rem] p-8 md:p-12 shadow-xl overflow-hidden" data-id="element-385">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" data-id="element-386" />

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cta-500 text-white text-sm font-bold mb-8 shadow-md uppercase tracking-wider" data-id="element-387">
            <GlitterHeart size={16} variant="light" data-id="element-388" />
            Flagship Course
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-389">
            <div data-id="element-390">
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-plum-700 mb-4" data-id="element-391">
                The Broken Beginning
              </h2>
              <p className="text-xl text-cta-500 font-bold mb-6" data-id="element-392">
                Your first step from surviving to thriving.
              </p>
              <p className="text-plum-700 leading-relaxed mb-8 font-medium" data-id="element-393">
                A course for anyone who's been knocked down by life and is ready
                to get back up — stronger, sassier, and unapologetically
                themselves. This isn't about toxic positivity. It's about doing
                the real work to rebuild your confidence, rewrite your story,
                and step into the badass you were always meant to be.
              </p>

              <a href="https://trippinwithmandy.gumroad.com/l/bgkefq" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 w-full md:w-auto px-8 py-4 rounded-full bg-plum-700 text-white font-bold text-lg hover:bg-plum-900 transition-all shadow-lg hover:-translate-y-1" data-id="element-394">
                <GlitterHeart size={20} variant="light" data-id="element-395" />
                Enroll on Gumroad
              </a>
            </div>

            <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-8 border border-white" data-id="element-396">
              <h3 className="font-bold text-plum-700 text-xl mb-6" data-id="element-397">
                What you'll learn:
              </h3>
              <ul className="space-y-4" data-id="element-398">
                {['How to stop letting your past define your future', 'Building unshakeable confidence from the inside out', 'Setting boundaries without guilt', 'Turning your pain into your superpower', 'Daily mindset practices that actually stick'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-399">
                    <div className="mt-1 bg-blush-200 rounded-full p-1 shrink-0" data-id="element-400">
                      <CheckIcon className="w-4 h-4 text-plum-700" data-id="element-401" />
                    </div>
                    <span className="text-plum-700 font-medium" data-id="element-402">{feature}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Coming Soon Courses */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-403">
        <div className="text-center mb-12" data-id="element-404">
          <h2 className="font-heading text-3xl font-bold text-plum-700" data-id="element-405">
            More Courses Dropping Soon
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-406">
          {[{
          title: 'Confidence Unleashed',
          desc: 'Stop playing small and start owning your worth in every room you walk into.',
          color: 'from-lavender-50 to-white',
          borderColor: 'border-lavender-200'
        }, {
          title: "Self-Care Isn't Selfish",
          desc: 'A practical guide to filling your own cup when you are used to pouring for everyone else.',
          color: 'from-sky-50 to-white',
          borderColor: 'border-sky-200'
        }].map((course, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.2
        }} className={`relative bg-gradient-to-br ${course.color} border ${course.borderColor} rounded-[2rem] p-8 shadow-sm`} data-id="element-407">
              <div className="absolute top-6 right-6 px-3 py-1 bg-white/80 text-plum-500 text-xs font-bold rounded-full uppercase tracking-wider flex items-center gap-1 border border-plum-100" data-id="element-408">
                <LockIcon className="w-3 h-3" data-id="element-409" /> Coming Soon
              </div>

              <h3 className="font-heading text-2xl font-bold text-plum-700 mb-3 mt-4" data-id="element-410">
                {course.title}
              </h3>
              <p className="text-plum-500 mb-8 font-medium" data-id="element-411">{course.desc}</p>

              <button className="w-full py-3 rounded-full font-bold bg-white text-plum-700 border border-plum-200 hover:bg-plum-50 transition-colors inline-flex items-center justify-center gap-2" data-id="element-412">
                <GlitterHeart size={14} data-id="element-413" />
                Get Notified
              </button>
            </motion.div>)}
        </div>

        <div className="mt-16 text-center text-plum-400 font-medium text-sm" data-id="element-414">
          <p data-id="element-415">
            All courses are hosted securely on Gumroad. Click any active course
            to be taken to the enrollment page.
          </p>
        </div>
      </section>
    </div>;
}